// Uncanny Valley Web Game Prototype 
import React, { useState, useEffect, useRef } from "react";
import { ref, listAll, getDownloadURL } from "firebase/storage";
import { storage } from "./firebase";
import "./UncannyValleyGame.css";
import { db } from "./firebase";
import { doc, getDoc, setDoc, updateDoc, increment } from "firebase/firestore";


const formatLabel = (label) => {
  const sentence = label.replace(/\./g, ' ');
  return sentence.charAt(0).toUpperCase() + sentence.slice(1);
};


const parseVideoName = (filename) => {
  const [sub1, role, setting, sub2, color, format] = filename.replace(".mp4", "").split("_");
  return { sub1, role, setting, sub2, color, format };
};

const incrementUncannyScore = async (filename) => {
  try {
    const ref = doc(db, "uncannyScores", filename);
    const snapshot = await getDoc(ref);

    if (snapshot.exists()) {
      await updateDoc(ref, {
        score: increment(1),
      });
      console.log(`Incremented score for ${filename}`);
    } else {
      await setDoc(ref, { score: 1 });
      console.log(`Created new score document for ${filename}`);
    }
  } catch (error) {
    console.error("Error updating Firestore score:", error);
  }
};



const fetchVideoList = async (mode, selectedDeck = null) => {
  const folders = mode === "advanced"
    ? ["videos_A", "videos_B"]
    : [selectedDeck || "videos_A"]; // fallback

  const allFiles = [];
  for (const folder of folders) {
    const folderRef = ref(storage, folder);
    const result = await listAll(folderRef);
    const files = result.items.filter((item) => item.name.endsWith(".mp4"));
    const list = await Promise.all(
      files.map(async (item) => {
        const url = await getDownloadURL(item);
        return { filename: item.name, url };
      })
    );
    allFiles.push(...list);
  }
  return allFiles;
};

const pickTwoNewVideos = (allVideos, currentVideos) => {
  const currentNames = currentVideos.map((v) => v.filename);
  const available = allVideos.filter((v) => !currentNames.includes(v.filename));
  const shuffled = [...available].sort(() => 0.5 - Math.random());
  return shuffled.slice(0, 2);
};

const findMatch = (name1, name2) => {
  const attrs1 = parseVideoName(name1);
  const attrs2 = parseVideoName(name2);
  for (const key of Object.keys(attrs1)) {
    if (attrs1[key] === attrs2[key]) return { key, value: attrs1[key] };
  }
  return undefined;
};

const getSubjectValues = (allVideos) => {
  const values = new Set();
  allVideos.forEach((video) => {
    const attrs = parseVideoName(video.filename);
    values.add(attrs.sub1);
    values.add(attrs.sub2);
  });
  return Array.from(values).sort();
};
const allAttributesMatch = (name1, name2) => {
  const attrs1 = parseVideoName(name1);
  const attrs2 = parseVideoName(name2);
  return Object.keys(attrs1).every((key) => attrs1[key] === attrs2[key]);
};


export default function UncannyValleyGame() {
  const storedMode = localStorage.getItem("uv-mode") || "basic";
  const [mode, setMode] = useState(storedMode);
  const [allVideos, setAllVideos] = useState([]);
  const [videos, setVideos] = useState([]);
  const [selected, setSelected] = useState([]);
  const [points, setPoints] = useState(0);
  const [message, setMessage] = useState("");
  const [round, setRound] = useState(1);
  const [hint, setHint] = useState(null);
  const [hintCount, setHintCount] = useState(0);
  const [countdown, setCountdown] = useState(10);
  const [hintMode, setHintMode] = useState(false);
  const [hintPair, setHintPair] = useState([]);
  const videoRefs = useRef([]);
  const [isPaused, setIsPaused] = useState(false);
  const [showConfirmRestart, setShowConfirmRestart] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [gameOver, setGameOver] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [firstMatch, setFirstMatch] = useState(null);
  const [firstSelectedPair, setFirstSelectedPair] = useState([]);
  const [firstMatchKey, setFirstMatchKey] = useState("");
  const [gameLocked, setGameLocked] = useState(false);
  const [matchHistory, setMatchHistory] = useState([]);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const [showFinalMessage, setShowFinalMessage] = useState(false);
  const [deckChoice, setDeckChoice] = useState(null);


  const getUncannyScores = () => {
    const data = localStorage.getItem("uncannyScores");
    return data ? JSON.parse(data) : {};
  };
  

  useEffect(() => {
    localStorage.setItem("uv-mode", mode);
  
    const selectedDeck =
      mode === "advanced"
        ? null
        : deckChoice || (Math.random() < 0.5 ? "videos_A" : "videos_B");
  
    if (!deckChoice && mode === "basic") {
      setDeckChoice(selectedDeck);
    }
  
    (async () => {
      const list = await fetchVideoList(mode, selectedDeck);
      setAllVideos(list);
      const shuffled = [...list].sort(() => 0.5 - Math.random());
      setVideos(shuffled.slice(0, 4));
    })();
  }, [mode, deckChoice]);
  
  

  useEffect(() => {
    const interval = setInterval(() => {
      setCountdown((prev) => (prev <= 1 ? 10 : prev - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (countdown === 1) setHintCount((prev) => prev + 1);
  }, [countdown]);

  const handleCardClick = (index) => {
    if (gameOver || gameLocked) return;
    if (hintMode) {
      const newHintPair = [...hintPair, index];
      setHintPair(newHintPair);
      if (newHintPair.length === 2) {
        const attrs1 = parseVideoName(videos[newHintPair[0]].filename);
        const attrs2 = parseVideoName(videos[newHintPair[1]].filename);
        if (attrs1[hint.key] === hint.value && attrs2[hint.key] === hint.value) {
          setPoints((p) => {
            const newPoints = p + 1;
            if (newPoints >= 11) setGameOver(true); // set win end state here xxx
            return newPoints;
          });
          const newVideos = pickTwoNewVideos(allVideos, videos);
          const updated = [...videos];
          [newHintPair[0], newHintPair[1]].forEach(
            (idx, j) => (updated[idx] = { ...newVideos[j], animate: true })
          );
          setVideos(updated);
          setRound((r) => r + 1);
          setFeedbackMessage("+1");
          setTimeout(() => setFeedbackMessage(""), 1500);
          setHint(null);
          setHintMode(false);
          setHintPair([]);
          setCountdown(10); // reset countdown when new round begins
        } else {
          setPoints((p) => p - 1);
          setHintPair([]);
          setFeedbackMessage("−1");
          setTimeout(() => setFeedbackMessage(""), 1500);
        }
      }
    } else {
      const newSelection = selected.includes(index)
        ? selected.filter((i) => i !== index)
        : selected.length === 2
        ? [selected[1], index]
        : [...selected, index];
      setSelected(newSelection);
      setMessage("");
    }
  };

  const handleValueClick = (value, selectedKey) => {
    if (gameOver || gameLocked) return;
    const [i1, i2] = selected;
    const name1 = videos[i1].filename;
    const name2 = videos[i2].filename;
    const match = findMatch(name1, name2);
    const correct = match && match.value.toLowerCase() === value.toLowerCase();
    setHintCount(0);
    setCountdown(10);

  
    if (mode === "advanced" && allAttributesMatch(name1, name2)) {
      const matchingKeys = Object.keys(parseVideoName(name1)).filter(
        (key) => parseVideoName(name1)[key] === parseVideoName(name2)[key]
      );
    
      if (matchingKeys.includes(selectedKey)) {
        setPoints((p) => p - 5);
        setFeedbackMessage("−5 (Whoops. Avoid siblings!)");
      } else {
        setPoints((p) => p - 1);
        setFeedbackMessage("−1 (Wrong guess)");
      }
    
      setTimeout(() => setFeedbackMessage(""), 1500);
      setSelected([]);
      return;
    }
    
  
    if (correct) {
      setMatchHistory(prev => [...prev, { pair: [videos[i1], videos[i2]], key: match.key }]);
      if (!firstSelectedPair.length) {
        setFirstSelectedPair([
          videos[i1].filename,
          videos[i2].filename
        ]);
        setFirstMatchKey(match.key);
      }          
      if (!firstMatch) {
        setFirstMatch({
          video1: videos[i1],
          video2: videos[i2],
          key: match.key,
          value: match.value
        });
      } 
      setPoints((p) => {
        if (!firstMatch) {
          setFirstMatch({
            video1: videos[i1],
            video2: videos[i2],
            key: match.key,
            value: match.value
          });
        }        
        const newPoints = p + 2;
        if (newPoints >= 11) setGameOver(true); // set win state here xxx
        return newPoints;
      });
      const newVideos = pickTwoNewVideos(allVideos, videos);
      const updated = [...videos];
      [i1, i2].forEach((idx, j) => (updated[idx] = { ...newVideos[j], animate: true }));
      setVideos(updated);
      setRound((r) => r + 1);
      setHint(null);
      setFeedbackMessage("+2");
      setCountdown(10); // reset timer on successful guess
    } else {
      setPoints((p) => p - 1);
      setFeedbackMessage("−1");
    }
    setTimeout(() => setFeedbackMessage(""), 1500);
    setSelected([]);
  };

  const togglePlayback = () => {
    const playing = !isPaused;
    videoRefs.current.forEach((vid) => vid && (playing ? vid.pause() : vid.play()));
    setIsPaused(playing);
  };

  const showHint = () => {
    if (hintCount < 1 || videos.length < 2 || gameOver || hintMode || gameLocked) return;

  
    // Clear any selected videos
    setSelected([]);
  
    for (let i = 0; i < videos.length; i++) {
      for (let j = i + 1; j < videos.length; j++) {
        const match = findMatch(videos[i].filename, videos[j].filename);
        if (match) {
          setHint(match);
          setHintMode(true);
          setHintPair([]);
          setCountdown(0); // stop timer when hint is used
          setFeedbackMessage("Hint: +1 if matched");
          setTimeout(() => setFeedbackMessage(""), 1500);
          setHintCount(0);        // Reset hint count to prevent reuse this round
          setCountdown(10);       // Restart countdown for next round
          return;
        }
      }
    }
  };
  
  

  const shuffleVideos = () => {
    const shuffled = [...allVideos].sort(() => 0.5 - Math.random());
    setVideos(shuffled.slice(0, 4));
    setSelected([]);
    setHint(null);
    setHintMode(false);
    setHintPair([]);
    setMessage("");
    setHintCount(0);
    setCountdown(10);
    setRound((r) => r + 1);
  };

  const confirmRestart = () => setShowConfirmRestart(true);
  const cancelRestart = () => setShowConfirmRestart(false);
  const restartGame = () => {
  const selectedDeck =
    mode === "advanced"
    ? null
    : Math.random() < 0.5 ? "videos_A" : "videos_B";
setDeckChoice(selectedDeck);


    setPoints(0);
    setRound(1);
    setHintCount(0);
    setCountdown(10);
    setSelected([]);
    setHint(null);
    setHintMode(false);
    setHintPair([]);
    setMessage("");
    setGameOver(false);
    setGameLocked(false);
    setFirstSelectedPair([]);         // 👈 Reset the first matched pair
    setFirstMatchKey("");             // 👈 Reset the first match key
    setMatchHistory([]);              // 👈 Reset all matched pairs
    setCurrentMatchIndex(0);          // 👈 Start from first match again
    setShowFinalMessage(false);       // 👈 Prevent skipping to end screen
  
  (async () => {
    const list = await fetchVideoList(mode, selectedDeck);
    setAllVideos(list);
    const shuffled = [...list].sort(() => 0.5 - Math.random());
    setVideos(shuffled.slice(0, 4));
    setShowConfirmRestart(false);
  })();
};

  const guessOptions = ["subject", "role", "setting", "color", "format"];
  const categoryColors = ["#e0f7fa", "#f1f8e9", "#fff3e0", "#fce4ec", "#ede7f6"];

  return (
    <div className="game-container" style={{ display: "flex", flexDirection: "row" }}>
      <div className="control-panel" style={{ width: "220px", paddingRight: "1rem", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
        <div>
          <h1>uncanNny VALLeY</h1>
          <label><input type="radio" value="basic" checked={mode === "basic"} onChange={() => setMode("basic")} /> Basic</label><br />
          <label><input type="radio" value="advanced" checked={mode === "advanced"} onChange={() => setMode("advanced")} /> Advanced</label>
          <p><strong>Round:</strong> {round}</p>
          <p><strong>Points:</strong> {points}</p>

        
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", marginTop: "1rem" }}>
          <button onClick={togglePlayback}>Pause / Play</button>
          <button onClick={shuffleVideos}>Shuffle</button>
          <button style={{ backgroundColor: "#f8d7da" }} onClick={confirmRestart}>Restart</button>
        </div>
      </div>

      <button onClick={() => setShowRules(true)} style={{ position: "absolute", top: 10, right: 10 }}>
  Rules
</button>


      {showConfirmRestart && (
        <div className="modal-overlay">
          <div className="modal-box">
            <p>Restart game?</p>
            <div style={{ display: "flex", gap: "1rem" }}>
              <button onClick={restartGame}>Yes</button>
              <button onClick={cancelRestart}>No</button>
            </div>
          </div>
        </div>
      )}

      {gameOver && (
        <div className="modal-overlay">
          <div className="modal-box" style={{ position: "relative" }}>

          <button
                style={{ position: "absolute", top: 10, right: 10 }}
                onClick={() => 
                  {
                    setGameOver(false);
                    setMatchHistory([]);         // 👈 Clear old match history
                    setCurrentMatchIndex(0);     // 👈 Reset index
                    setShowFinalMessage(false);  // 👈 Prevent skipping to the end
                    setGameLocked(true);         // 👈 🔒 Freeze the game
                  }}
              >
                ❌
              </button>

          {!showFinalMessage ? (
            <>
              
              <h2>🎉 You Won!</h2>
              <p>It took you {round} rounds to reach 11 points.</p>

              {matchHistory.length > 0 && (
                <div style={{ marginTop: "1rem" }}>
                  <h3>Match {currentMatchIndex + 1} of {matchHistory.length}</h3>
                  <p>
                  <center><h3>Click to vote for the Uncanniest Image</h3></center>
                  </p>
                  <div style={{ display: "flex", gap: "1rem", marginTop: "0.5rem" }}>
                  {matchHistory[currentMatchIndex].pair.map((videoObj, i) => (
                    <video
                      key={i}
                      src={videoObj.url}

                        width="150"
                        autoPlay
                        loop
                        muted
                        playsInline
                        style={{ borderRadius: "8px", border: "1px solid #ccc", cursor: "pointer" }}
                        onClick={() => {
                          incrementUncannyScore(videoObj.filename); // 👈 track the vote
                          if (currentMatchIndex < matchHistory.length - 1) {
                            setCurrentMatchIndex(currentMatchIndex + 1);
                          } else {
                            setShowFinalMessage(true);
                          }
                        }}
                        
                      />
                    ))}
                  </div>
                  <p><center><h3>for the prompt:{" "}
                  {formatLabel(parseVideoName(matchHistory[currentMatchIndex].pair[0].filename)[matchHistory[currentMatchIndex].key])}


               </h3></center></p>
                </div>
              )}
              <button onClick={restartGame} style={{ marginTop: "1rem" }}>Play Again</button>
            </>
          ) : (
            <>
              <button
                style={{ position: "absolute", top: 10, right: 10 }}
                onClick={() => 
                  {
                    setGameOver(false);
                    setMatchHistory([]);         // 👈 Clear old match history
                    setCurrentMatchIndex(0);     // 👈 Reset index
                    setShowFinalMessage(false);  // 👈 Prevent skipping to the end
                    setGameLocked(true);         // 👈 🔒 Freeze the game
                  }}
              >
                ❌
              </button>
              <h2>Thanks for playing!</h2>
              <p>We hope you enjoyed finding the uncanniest images.</p>
              <button onClick={restartGame} style={{ marginTop: "1rem" }}>Play Again</button>
            </>
          )}



          </div>
        </div>
      )}




<div className="game-layout" style={{ display: "flex", flexDirection: "row", flexGrow: 1 }}>
  {feedbackMessage && (
    <div style={{
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      fontSize: "3rem",
      backgroundColor: "rgba(255,255,255,0.8)",
      padding: "1rem 2rem",
      borderRadius: "10px",
      zIndex: 10,
      pointerEvents: "none"
    }}>
      {feedbackMessage}
    </div>
  )}

  <div style={{ flex: 2, display: "flex", flexDirection: "column" }}>
    <h1 style={{ fontSize: "1.8rem", margin: "0 0 1rem 0" }}>1. Pick a Pair</h1>
    <div className="video-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", flex: 1 }}>
      {videos.map((vid, idx) => (
        <div
          key={idx}
          className={`video-card ${selected.includes(idx) || hintPair.includes(idx) ? "selected" : ""}`}
          onClick={() => handleCardClick(idx)}
        >
          <video
            ref={(el) => videoRefs.current[idx] = el}
            src={vid.url}
            className="video-player"
            autoPlay loop muted playsInline
          />
        </div>
      ))}
    </div>
  </div>

  <div style={{ width: "300px", paddingLeft: "1rem", display: "flex", flexDirection: "column" }}>
    <h1 style={{ fontSize: "1.8rem", margin: "0 0 1rem 0" }}>2. Guess the Prompt</h1>
    <div className="sidebar">
   
      {guessOptions.map((key, i) => {
        const values = key === "subject"
          ? getSubjectValues(allVideos)
          : Array.from(new Set(allVideos.map(v => parseVideoName(v.filename)[key])));
        return (
          <div key={key} style={{ backgroundColor: categoryColors[i], padding: "0.5rem", marginBottom: "1rem" }}>
            <div
              style={{
                display: "grid",
                gridTemplateRows: `repeat(${Math.ceil(values.length / 3)}, auto)`,
                gridAutoFlow: "column",
                gap: "0.5rem"
              }}
            >
              {values.sort().map((val) => (
                <button
                  key={val}
                  onClick={() => handleValueClick(val, key)}
                  disabled={selected.length !== 2}
                  className={
                    hint && 
                    hint.key === key && 
                    formatLabel(hint.value).toLowerCase() === formatLabel(val).toLowerCase()
                      ? "hint-blink"
                      : ""
                  }
                  
                >
                  {formatLabel(val)}
                </button>
              ))}
            </div>
          </div>
        );
      })}
      {message && <p>{message}</p>}
    </div>
  </div>
</div>

      {showRules && (
  <div className="modal-overlay">
    <div className="modal-box">
      <h2>Game Rules: {mode === "basic" ? "Basic" : "Advanced"}</h2>
      {mode === "basic" ? (
        <>
          <p><b>First, select any two video cards.</b><br></br>Each video card is composed of six elements.<br></br>Each card shares one element with every other card in its deck (and only one).</p>
          <p><b>Second, pick a prompt.</b> <br></br>These prompts were given to an AI to render. View the cards from the lens of the AI.<br></br> Notice the uncanny valley separating the real world from the vision of an AI.</p>
          <p>+2 points for a correct guess<br></br>−1 point for a wrong guess</p>
          <p>Game ends at 11 points!</p>
        </>
      ) : (
        <>
        <p><b>First, select any two video cards.</b><br></br>Each video card is composed of six elements.<br></br>Each card shares one element with every other card in its deck (and only one).</p>
        <p><b>Second, pick a prompt.</b> <br></br>These prompts were given to an AI to render. View the cards from the lens of the AI.<br></br> Notice the uncanny valley separating the real world from the vision of an AI.</p>
        <p>In the <b>advance game</b> every card has one sibling, in which all elements will match.<br></br>Avoid picking a prompt for siblings!</p>
        <p>+2 points for a correct guess<br></br>−1 point for a wrong guess</p>
        <p>Game ends at 11 points!</p>
      </>
      )}
      <button onClick={() => setShowRules(false)}>Close</button>
    </div>
  </div>
)}

    </div>
  );
}
